import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  FileText, Plus, Search, Download, CheckCircle2, XCircle, Clock, 
  AlertCircle, Printer, Eye, Trash2, Ban, Archive, Building, Loader2,
  ChevronDown, ChevronUp, RefreshCw, DollarSign, CreditCard, TrendingUp, TrendingDown
} from 'lucide-react';
import Pagination from '../components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BANK_OPTIONS = ['RBC', 'TD', 'CIBC', 'BMO', 'Scotiabank'];

export default function ChequeManagement() {
  const [activeTab, setActiveTab] = useState('cheques');
  const [chequeBooks, setChequeBooks] = useState([]);
  const [cheques, setCheques] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [outstanding, setOutstanding] = useState({ cheques: [], total: 0 });
  const [depositsInTransit, setDepositsInTransit] = useState({ deposits: [], total: 0 });
  const [arApSummary, setArApSummary] = useState(null);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  
  // Filters
  const [filterBank, setFilterBank] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Modals
  const [showBookModal, setShowBookModal] = useState(false);
  const [showChequeModal, setShowChequeModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [showPayInvoiceModal, setShowPayInvoiceModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [selectedCheque, setSelectedCheque] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  
  // Form data
  const [bookForm, setBookForm] = useState({
    bank_account_id: '', bank_name: 'RBC', starting_number: 1, ending_number: 100, stock_type: 'blank'
  });
  const [chequeForm, setChequeForm] = useState({
    cheque_book_id: '', payee_name: '', amount: '', memo: '', date: '', supplier_id: '', invoice_id: ''
  });
  const [depositForm, setDepositForm] = useState({
    bank_account_id: '', deposit_date: new Date().toISOString().split('T')[0], items: [], notes: ''
  });
  const [depositItem, setDepositItem] = useState({ type: 'cheque', amount: '', source: '', cheque_number: '' });
  const [approvalNotes, setApprovalNotes] = useState('');
  const [payInvoiceForm, setPayInvoiceForm] = useState({ cheque_book_id: '', memo: '' });
  const [voidReason, setVoidReason] = useState('');

  useEffect(() => { fetchBankAccounts(); fetchChequeBooks(); }, []);
  
  useEffect(() => { 
    if (activeTab === 'cheques') fetchCheques();
    if (activeTab === 'books') fetchChequeBooks();
    if (activeTab === 'deposits') fetchDeposits();
    if (activeTab === 'reconciliation') { fetchOutstanding(); fetchDepositsInTransit(); fetchArApSummary(); }
    if (activeTab === 'payables') fetchUnpaidInvoices();
  }, [activeTab, pagination.page, filterBank, filterStatus]);

  const fetchBankAccounts = async () => {
    try {
      const res = await axios.get(`${API}/bank-accounts`);
      setBankAccounts(res.data);
    } catch (error) { console.error('Failed to fetch bank accounts:', error); }
  };

  const fetchChequeBooks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cheques/books`);
      setChequeBooks(res.data);
    } catch (error) { console.error('Failed to fetch cheque books:', error); }
    finally { setLoading(false); }
  };

  const fetchArApSummary = async () => {
    try {
      const res = await axios.get(`${API}/cheques/ar-ap-summary`);
      setArApSummary(res.data);
    } catch (error) { console.error('Failed to fetch AR/AP summary:', error); }
  };

  const fetchUnpaidInvoices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cheques/unpaid-invoices`);
      setUnpaidInvoices(res.data.invoices || []);
    } catch (error) { console.error('Failed to fetch unpaid invoices:', error); }
    finally { setLoading(false); }
  };

  const fetchCheques = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (filterStatus) params.append('status', filterStatus);
      
      const res = await axios.get(`${API}/cheques?${params}`);
      if (res.data.data) {
        setCheques(res.data.data);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
      } else {
        setCheques(res.data);
      }
    } catch (error) { console.error('Failed to fetch cheques:', error); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.limit, filterStatus]);

  const fetchDeposits = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      
      const res = await axios.get(`${API}/cheques/deposits?${params}`);
      if (res.data.data) {
        setDeposits(res.data.data);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
      } else {
        setDeposits(res.data);
      }
    } catch (error) { console.error('Failed to fetch deposits:', error); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.limit]);

  const fetchOutstanding = async () => {
    try {
      const res = await axios.get(`${API}/cheques/outstanding`);
      setOutstanding({ cheques: res.data.cheques, total: res.data.total_outstanding });
    } catch (error) { console.error('Failed to fetch outstanding:', error); }
  };

  const fetchDepositsInTransit = async () => {
    try {
      const res = await axios.get(`${API}/cheques/deposits-in-transit`);
      setDepositsInTransit({ deposits: res.data.deposits, total: res.data.total_in_transit });
    } catch (error) { console.error('Failed to fetch deposits in transit:', error); }
  };

  const createChequeBook = async () => {
    try {
      await axios.post(`${API}/cheques/books`, bookForm);
      setShowBookModal(false);
      setBookForm({ bank_account_id: '', bank_name: 'RBC', starting_number: 1, ending_number: 100, stock_type: 'blank' });
      fetchChequeBooks();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create cheque book');
    }
  };

  const createCheque = async () => {
    try {
      await axios.post(`${API}/cheques`, { ...chequeForm, amount: parseFloat(chequeForm.amount) });
      setShowChequeModal(false);
      setChequeForm({ cheque_book_id: '', payee_name: '', amount: '', memo: '', date: '', supplier_id: '', invoice_id: '' });
      fetchCheques();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create cheque');
    }
  };

  const approveCheque = async (approved) => {
    try {
      await axios.post(`${API}/cheques/${selectedCheque.id}/approve`, { approved, notes: approvalNotes });
      setShowApprovalModal(false);
      setSelectedCheque(null);
      setApprovalNotes('');
      fetchCheques();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to approve cheque');
    }
  };

  const voidCheque = async () => {
    try {
      await axios.post(`${API}/cheques/${selectedCheque.id}/void`, { reason: voidReason });
      setShowVoidModal(false);
      setSelectedCheque(null);
      setVoidReason('');
      fetchCheques();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to void cheque');
    }
  };

  const payInvoiceByCheque = async () => {
    try {
      await axios.post(`${API}/cheques/pay-invoice`, {
        invoice_id: selectedInvoice.id,
        cheque_book_id: payInvoiceForm.cheque_book_id,
        memo: payInvoiceForm.memo
      });
      setShowPayInvoiceModal(false);
      setSelectedInvoice(null);
      setPayInvoiceForm({ cheque_book_id: '', memo: '' });
      fetchUnpaidInvoices();
      fetchCheques();
      alert('Cheque created! It requires manager approval before printing.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create cheque');
    }
  };

  const viewCheque = async (cheque, format = 'single') => {
    try {
      const res = await axios.get(`${API}/cheques/${cheque.id}/pdf?format=${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      setPreviewTitle(`Cheque #${cheque.cheque_number} - ${cheque.payee_name}`);
      setSelectedCheque(cheque);
      setShowPreviewModal(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate preview');
    }
  };

  const viewDeposit = async (deposit) => {
    try {
      const res = await axios.get(`${API}/cheques/deposits/${deposit.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      setPreviewTitle(`Deposit Slip #${deposit.deposit_number}`);
      setSelectedDeposit(deposit);
      setShowPreviewModal(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate preview');
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewTitle('');
    setShowPreviewModal(false);
    setSelectedCheque(null);
    setSelectedDeposit(null);
  };

  const printFromPreview = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
      if (selectedCheque) {
        fetchCheques(); // Refresh to update printed status
      }
    }
  };

  const printCheque = async (chequeId, format = 'single') => {
    try {
      const res = await axios.get(`${API}/cheques/${chequeId}/pdf?format=${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      fetchCheques();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate PDF');
    }
  };

  const createDeposit = async () => {
    try {
      await axios.post(`${API}/cheques/deposits`, depositForm);
      setShowDepositModal(false);
      setDepositForm({ bank_account_id: '', deposit_date: new Date().toISOString().split('T')[0], items: [], notes: '' });
      fetchDeposits();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create deposit');
    }
  };

  const addDepositItem = () => {
    if (!depositItem.amount || !depositItem.source) return;
    setDepositForm(prev => ({
      ...prev,
      items: [...prev.items, { ...depositItem, amount: parseFloat(depositItem.amount) }]
    }));
    setDepositItem({ type: 'cheque', amount: '', source: '', cheque_number: '' });
  };

  const removeDepositItem = (index) => {
    setDepositForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const printDepositSlip = async (depositId) => {
    try {
      const res = await axios.get(`${API}/cheques/deposits/${depositId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate PDF');
    }
  };

  const markDeposited = async (depositId) => {
    try {
      await axios.post(`${API}/cheques/deposits/${depositId}/mark-deposited`);
      fetchDeposits();
      fetchDepositsInTransit();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to mark as deposited');
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount || 0);

  const getStatusBadge = (status) => {
    const badges = {
      pending_approval: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending Approval' },
      issued: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Issued' },
      printed: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Printed' },
      cleared: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Cleared' },
      voided: { bg: 'bg-red-100', text: 'text-red-700', label: 'Voided' },
      rejected: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Rejected' },
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
      deposited: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Deposited' },
      reconciled: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Reconciled' }
    };
    const badge = badges[status] || { bg: 'bg-slate-100', text: 'text-slate-700', label: status };
    return <span className={`px-2 py-1 text-xs rounded-full ${badge.bg} ${badge.text}`}>{badge.label}</span>;
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  return (
    <div className="space-y-6" data-testid="cheque-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cheque Management & Deposits</h1>
          <p className="text-sm text-slate-500">Manage cheque books, issue cheques, and prepare deposit slips</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-6 overflow-x-auto">
          {[
            { id: 'cheques', label: 'Cheques', icon: FileText },
            { id: 'payables', label: 'Pay Invoices (AP)', icon: CreditCard },
            { id: 'books', label: 'Cheque Books', icon: Archive },
            { id: 'deposits', label: 'Deposit Slips', icon: Building },
            { id: 'reconciliation', label: 'AR/AP Summary', icon: DollarSign }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-1 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'border-indigo-600 text-indigo-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* CHEQUES TAB */}
      {activeTab === 'cheques' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-3">
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
                className="px-4 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">All Status</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="issued">Issued</option>
                <option value="printed">Printed</option>
                <option value="cleared">Cleared</option>
                <option value="voided">Voided</option>
              </select>
            </div>
            <button
              onClick={() => setShowChequeModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              data-testid="new-cheque-btn"
            >
              <Plus className="w-4 h-4" /> Issue Cheque
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Cheque #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Payee</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Bank</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {cheques.length === 0 ? (
                        <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500">No cheques found</td></tr>
                      ) : (
                        cheques.map((cheque) => (
                          <tr key={cheque.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 font-mono font-semibold text-slate-900">{cheque.cheque_number}</td>
                            <td className="px-4 py-3 text-sm text-slate-600">{cheque.date}</td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-slate-900">{cheque.payee_name}</p>
                              {cheque.memo && <p className="text-xs text-slate-500">{cheque.memo}</p>}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">{cheque.bank_name}</td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatCurrency(cheque.amount)}</td>
                            <td className="px-4 py-3">{getStatusBadge(cheque.status)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                {cheque.status === 'pending_approval' && (
                                  <button
                                    onClick={() => { setSelectedCheque(cheque); setShowApprovalModal(true); }}
                                    className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                                    title="Approve"
                                  >
                                    <CheckCircle2 className="w-4 h-4" />
                                  </button>
                                )}
                                {['issued', 'printed'].includes(cheque.status) && (
                                  <>
                                    <button
                                      onClick={() => viewCheque(cheque, 'single')}
                                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                      title="View Cheque"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => printCheque(cheque.id, 'single')}
                                      className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                                      title="Print Single"
                                    >
                                      <Printer className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => viewCheque(cheque, 'voucher_3')}
                                      className="p-1 text-violet-600 hover:bg-violet-50 rounded"
                                      title="View 3-per-page"
                                    >
                                      <FileText className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                                {cheque.status !== 'voided' && cheque.status !== 'cleared' && (
                                  <button
                                    onClick={() => { setSelectedCheque(cheque); setShowVoidModal(true); }}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    title="Void"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              </>
            )}
          </div>
        </div>
      )}

      {/* CHEQUE BOOKS TAB */}
      {activeTab === 'books' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowBookModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              data-testid="new-book-btn"
            >
              <Plus className="w-4 h-4" /> New Cheque Book
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {chequeBooks.map(book => (
              <div key={book.id} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">{book.bank_name}</h3>
                    <p className="text-sm text-slate-500">{book.entity_name}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${book.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                    {book.status}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Range:</span>
                    <span className="font-mono">{book.starting_number} - {book.ending_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Current:</span>
                    <span className="font-mono font-semibold">{book.current_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Used:</span>
                    <span>{book.used_count} / {book.total_cheques}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Voided:</span>
                    <span className="text-red-600">{book.voided_count}</span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full" 
                      style={{ width: `${(book.used_count / book.total_cheques) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1 text-center">
                    {book.total_cheques - book.used_count} remaining
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DEPOSITS TAB */}
      {activeTab === 'deposits' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowDepositModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              data-testid="new-deposit-btn"
            >
              <Plus className="w-4 h-4" /> New Deposit Slip
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Deposit #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Bank Account</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Items</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {deposits.length === 0 ? (
                        <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500">No deposits found</td></tr>
                      ) : (
                        deposits.map((deposit) => (
                          <tr key={deposit.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 font-mono font-semibold text-slate-900">#{deposit.deposit_number}</td>
                            <td className="px-4 py-3 text-sm text-slate-600">{deposit.deposit_date}</td>
                            <td className="px-4 py-3">
                              <p className="text-sm text-slate-900">{deposit.entity_name}</p>
                              <p className="text-xs text-slate-500">{deposit.bank_name} - {deposit.account_number}</p>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm">{deposit.item_count} items</span>
                              <span className="text-xs text-slate-500 block">({deposit.cheque_count} cheques)</span>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatCurrency(deposit.total_amount)}</td>
                            <td className="px-4 py-3">{getStatusBadge(deposit.status)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => viewDeposit(deposit)}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  title="View Deposit Slip"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => printDepositSlip(deposit.id)}
                                  className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                                  title="Print Slip"
                                >
                                  <Printer className="w-4 h-4" />
                                </button>
                                {deposit.status === 'pending' && (
                                  <button
                                    onClick={() => markDeposited(deposit.id)}
                                    className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                                    title="Mark Deposited"
                                  >
                                    <CheckCircle2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              </>
            )}
          </div>
        </div>
      )}

      {/* PAYABLES TAB - Pay Invoices by Cheque */}
      {activeTab === 'payables' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-100 p-4">
            <div className="flex items-center gap-4">
              <TrendingDown className="w-8 h-8 text-red-500" />
              <div>
                <h3 className="font-semibold text-slate-900">Accounts Payable</h3>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(unpaidInvoices.reduce((sum, inv) => sum + inv.total_amount - (inv.amount_paid || 0), 0))}
                </p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-sm text-slate-500">Unpaid Invoices</p>
                <p className="text-xl font-semibold">{unpaidInvoices.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Invoice</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Supplier</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Due Date</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount Due</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Pay</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {unpaidInvoices.length === 0 ? (
                      <tr><td colSpan="6" className="px-4 py-12 text-center text-slate-500">No unpaid invoices</td></tr>
                    ) : (
                      unpaidInvoices.map((invoice) => (
                        <tr key={invoice.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-mono text-sm font-semibold text-slate-900">{invoice.invoice_number}</td>
                          <td className="px-4 py-3 text-sm text-slate-900">{invoice.supplier_name || 'Unknown'}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{invoice.description}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{invoice.due_date}</td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-900">
                            {formatCurrency(invoice.total_amount - (invoice.amount_paid || 0))}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => { setSelectedInvoice(invoice); setShowPayInvoiceModal(true); }}
                              className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                              data-testid={`pay-invoice-${invoice.id}`}
                            >
                              Pay by Cheque
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* RECONCILIATION TAB */}
      {activeTab === 'reconciliation' && (
        <div className="space-y-6">
          {/* AR/AP Summary Cards */}
          {arApSummary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-emerald-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm font-medium text-slate-600">Accounts Receivable</span>
                </div>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(arApSummary.accounts_receivable.total)}</p>
                <p className="text-xs text-slate-500 mt-1">Money owed to you</p>
              </div>
              <div className="bg-white rounded-xl border border-red-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  <span className="text-sm font-medium text-slate-600">Accounts Payable</span>
                </div>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(arApSummary.accounts_payable.total)}</p>
                <p className="text-xs text-slate-500 mt-1">Money you owe</p>
              </div>
              <div className="bg-white rounded-xl border border-amber-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-5 h-5 text-amber-500" />
                  <span className="text-sm font-medium text-slate-600">Outstanding Cheques</span>
                </div>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(arApSummary.accounts_payable.outstanding_cheques)}</p>
                <p className="text-xs text-slate-500 mt-1">Issued but not cleared</p>
              </div>
              <div className="bg-white rounded-xl border border-blue-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Building className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium text-slate-600">Deposits in Transit</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(arApSummary.accounts_receivable.deposits_in_transit)}</p>
                <p className="text-xs text-slate-500 mt-1">Pending clearance</p>
              </div>
            </div>
          )}

          {/* Outstanding Cheques & Deposits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Outstanding Cheques</h3>
                <span className="text-2xl font-bold text-amber-600">{formatCurrency(outstanding.total)}</span>
              </div>
              <p className="text-sm text-slate-500 mb-4">{outstanding.cheques.length} cheques issued but not yet cleared</p>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {outstanding.cheques.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-2 border-b border-slate-100">
                    <div>
                      <span className="font-mono text-sm">#{c.cheque_number}</span>
                      <span className="text-sm text-slate-500 ml-2">{c.payee_name}</span>
                    </div>
                    <span className="font-semibold">{formatCurrency(c.amount)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Deposits in Transit</h3>
                <span className="text-2xl font-bold text-blue-600">{formatCurrency(depositsInTransit.total)}</span>
              </div>
              <p className="text-sm text-slate-500 mb-4">{depositsInTransit.deposits.length} deposits pending clearance</p>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {depositsInTransit.deposits.map(d => (
                  <div key={d.id} className="flex items-center justify-between py-2 border-b border-slate-100">
                    <div>
                      <span className="font-mono text-sm">#{d.deposit_number}</span>
                      <span className="text-sm text-slate-500 ml-2">{d.deposit_date}</span>
                    </div>
                    <span className="font-semibold">{formatCurrency(d.total_amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Reconciliation Impact */}
          <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl border border-indigo-100 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Bank Reconciliation Impact</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-sm text-slate-600">Outstanding Cheques</p>
                <p className="text-xl font-bold text-amber-600">- {formatCurrency(outstanding.total)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-slate-600">Deposits in Transit</p>
                <p className="text-xl font-bold text-blue-600">+ {formatCurrency(depositsInTransit.total)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-slate-600">Net Adjustment</p>
                <p className="text-xl font-bold text-indigo-600">{formatCurrency(depositsInTransit.total - outstanding.total)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CREATE CHEQUE BOOK MODAL */}
      {showBookModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-slate-900 mb-4">New Cheque Book</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bank Account</label>
                <select
                  value={bookForm.bank_account_id}
                  onChange={(e) => setBookForm({ ...bookForm, bank_account_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                >
                  <option value="">Select Account</option>
                  {bankAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.entity_name} - {acc.account_number}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bank</label>
                <select
                  value={bookForm.bank_name}
                  onChange={(e) => setBookForm({ ...bookForm, bank_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  {BANK_OPTIONS.map(bank => (
                    <option key={bank} value={bank}>{bank}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Starting #</label>
                  <input
                    type="number"
                    value={bookForm.starting_number}
                    onChange={(e) => setBookForm({ ...bookForm, starting_number: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ending #</label>
                  <input
                    type="number"
                    value={bookForm.ending_number}
                    onChange={(e) => setBookForm({ ...bookForm, ending_number: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Stock Type</label>
                <select
                  value={bookForm.stock_type}
                  onChange={(e) => setBookForm({ ...bookForm, stock_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="blank">Blank Stock (MICR printing)</option>
                  <option value="pre_printed">Pre-printed Bank Stock</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowBookModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={createChequeBook} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Create</button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE CHEQUE MODAL */}
      {showChequeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Issue New Cheque</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cheque Book</label>
                <select
                  value={chequeForm.cheque_book_id}
                  onChange={(e) => setChequeForm({ ...chequeForm, cheque_book_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                >
                  <option value="">Select Cheque Book</option>
                  {chequeBooks.filter(b => b.status === 'active').map(book => (
                    <option key={book.id} value={book.id}>
                      {book.bank_name} - {book.entity_name} (Next: #{book.current_number})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payee Name</label>
                <input
                  type="text"
                  value={chequeForm.payee_name}
                  onChange={(e) => setChequeForm({ ...chequeForm, payee_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="Pay to the order of..."
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={chequeForm.amount}
                    onChange={(e) => setChequeForm({ ...chequeForm, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={chequeForm.date}
                    onChange={(e) => setChequeForm({ ...chequeForm, date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Memo</label>
                <input
                  type="text"
                  value={chequeForm.memo}
                  onChange={(e) => setChequeForm({ ...chequeForm, memo: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="For invoice #..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowChequeModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={createCheque} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Issue Cheque</button>
            </div>
          </div>
        </div>
      )}

      {/* APPROVAL MODAL */}
      {showApprovalModal && selectedCheque && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Approve Cheque #{selectedCheque.cheque_number}</h2>
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <p className="text-sm"><strong>Payee:</strong> {selectedCheque.payee_name}</p>
              <p className="text-sm"><strong>Amount:</strong> {formatCurrency(selectedCheque.amount)}</p>
              <p className="text-sm"><strong>Date:</strong> {selectedCheque.date}</p>
              <p className="text-sm"><strong>Created by:</strong> {selectedCheque.created_by_name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optional)</label>
              <textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowApprovalModal(false); setSelectedCheque(null); }} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={() => approveCheque(false)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Reject</button>
              <button onClick={() => approveCheque(true)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Approve</button>
            </div>
          </div>
        </div>
      )}

      {/* VOID MODAL */}
      {showVoidModal && selectedCheque && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-red-600 mb-4">Void Cheque #{selectedCheque.cheque_number}</h2>
            <div className="bg-red-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800">This action cannot be undone. The cheque will be permanently voided.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Reason for voiding *</label>
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows={2}
                placeholder="e.g., Duplicate payment, wrong amount..."
                required
              />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowVoidModal(false); setSelectedCheque(null); }} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={voidCheque} disabled={!voidReason} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">Void Cheque</button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE DEPOSIT MODAL */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-slate-900 mb-4">New Deposit Slip</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bank Account</label>
                  <select
                    value={depositForm.bank_account_id}
                    onChange={(e) => setDepositForm({ ...depositForm, bank_account_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  >
                    <option value="">Select Account</option>
                    {bankAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.entity_name} - {acc.account_number}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Deposit Date</label>
                  <input
                    type="date"
                    value={depositForm.deposit_date}
                    onChange={(e) => setDepositForm({ ...depositForm, deposit_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Add Item Form */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Add Item</h3>
                <div className="grid grid-cols-4 gap-3">
                  <select
                    value={depositItem.type}
                    onChange={(e) => setDepositItem({ ...depositItem, type: e.target.value })}
                    className="px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="cheque">Cheque</option>
                    <option value="cash">Cash</option>
                    <option value="money_order">Money Order</option>
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    value={depositItem.amount}
                    onChange={(e) => setDepositItem({ ...depositItem, amount: e.target.value })}
                    className="px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="Amount"
                  />
                  <input
                    type="text"
                    value={depositItem.source}
                    onChange={(e) => setDepositItem({ ...depositItem, source: e.target.value })}
                    className="px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="Source/Tenant"
                  />
                  <button
                    onClick={addDepositItem}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Add
                  </button>
                </div>
                {depositItem.type === 'cheque' && (
                  <input
                    type="text"
                    value={depositItem.cheque_number}
                    onChange={(e) => setDepositItem({ ...depositItem, cheque_number: e.target.value })}
                    className="mt-2 px-3 py-2 border border-slate-300 rounded-lg w-full"
                    placeholder="Cheque Number"
                  />
                )}
              </div>

              {/* Items List */}
              {depositForm.items.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Source</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Cheque #</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Amount</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {depositForm.items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2 text-sm capitalize">{item.type}</td>
                          <td className="px-3 py-2 text-sm">{item.source}</td>
                          <td className="px-3 py-2 text-sm font-mono">{item.cheque_number || '-'}</td>
                          <td className="px-3 py-2 text-sm text-right">{formatCurrency(item.amount)}</td>
                          <td className="px-3 py-2">
                            <button onClick={() => removeDepositItem(idx)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50">
                        <td colSpan="3" className="px-3 py-2 text-sm font-semibold">Total</td>
                        <td className="px-3 py-2 text-sm font-semibold text-right">
                          {formatCurrency(depositForm.items.reduce((sum, i) => sum + i.amount, 0))}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={depositForm.notes}
                  onChange={(e) => setDepositForm({ ...depositForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowDepositModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button 
                onClick={createDeposit} 
                disabled={!depositForm.bank_account_id || depositForm.items.length === 0}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Create Deposit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PAY INVOICE BY CHEQUE MODAL */}
      {showPayInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Pay Invoice by Cheque</h2>
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <p className="text-sm"><strong>Invoice:</strong> {selectedInvoice.invoice_number}</p>
              <p className="text-sm"><strong>Supplier:</strong> {selectedInvoice.supplier_name || 'Unknown'}</p>
              <p className="text-sm"><strong>Description:</strong> {selectedInvoice.description}</p>
              <p className="text-sm"><strong>Amount Due:</strong> {formatCurrency(selectedInvoice.total_amount - (selectedInvoice.amount_paid || 0))}</p>
              <p className="text-sm"><strong>Due Date:</strong> {selectedInvoice.due_date}</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Select Cheque Book *</label>
                <select
                  value={payInvoiceForm.cheque_book_id}
                  onChange={(e) => setPayInvoiceForm({ ...payInvoiceForm, cheque_book_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                >
                  <option value="">Select Cheque Book</option>
                  {chequeBooks.filter(b => b.status === 'active').map(book => (
                    <option key={book.id} value={book.id}>
                      {book.bank_name} - {book.entity_name} (Next: #{book.current_number})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Memo (optional)</label>
                <input
                  type="text"
                  value={payInvoiceForm.memo}
                  onChange={(e) => setPayInvoiceForm({ ...payInvoiceForm, memo: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="Additional payment notes..."
                />
              </div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 mt-4">
              <p className="text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Cheque will require manager approval before printing.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowPayInvoiceModal(false); setSelectedInvoice(null); }} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button 
                onClick={payInvoiceByCheque} 
                disabled={!payInvoiceForm.cheque_book_id}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Create Cheque
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PREVIEW MODAL */}
      {showPreviewModal && previewUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-4xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">{previewTitle}</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={printFromPreview}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = previewUrl;
                    link.download = `${previewTitle.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
                    link.click();
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={closePreview}
                  className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 p-4 overflow-hidden">
              <iframe
                src={previewUrl}
                className="w-full h-full border border-slate-200 rounded-lg"
                title={previewTitle}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
